'use client';
import React, { useRef, useState } from 'react';

import { Text } from '~components/Typography';
import { useAuthActionsContext } from '~context/auth';
import { NsfwReason, RegisterInbound } from '~features/annals';
import { annals } from '~features/annals/hooks';
import { useLanguage } from '~features/language';
import { useSwitchSafeModeWithAgeValidation } from '~features/nsfw';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import DotDotDotIcon from '~public/assets/icons/dotdotdot.svg';
import LoginIcon from '~public/assets/icons/navigation/login.svg';
import WebIcon from '~public/assets/icons/navigation/web.svg';
import EyeNoneIcon from '~public/img/nsfw/eye-none.svg';
import EyeOpenIcon from '~public/img/nsfw/eye-open.svg';
import { Dropdown, DropdownElement } from '~shared/ui/atoms';
import { DropdownSeparator } from '~shared/ui/atoms/Dropdown';
import { ButtonIconGhost, ButtonPrimary } from '~shared/ui/molecules';
import { useHeaderContext } from '~widgets/Header/context';

import {
	MobileDropdownElement,
	MobileDropdownSeparator,
	NSFWButton,
} from './styles';

export const NotAuthContent = () => {
	const { openRegister, openLogin } = useAuthActionsContext();
	const { languageOpen } = useHeaderContext();
	const { isSafeMode } = useSession();
	const { language, locale } = useLanguage();
	const t = useTranslations('ProfileMenu');
	const [loadingNSFW, setLoadingNSFW] = useState(false);
	const dropdownRef = useRef<HTMLButtonElement | null>(null);
	const annalsNavLoginClicked = annals.navigation.useNavTopLoginButtonClicked();

	const handleRegister = () => {
		openRegister({
			analytics: { inbound: RegisterInbound.HeaderClickAction },
		});
	};
	const updateSafeMode = useSwitchSafeModeWithAgeValidation();

	const handleSwitchModeClick = async () => {
		try {
			setLoadingNSFW(true);
			await updateSafeMode(!isSafeMode, {
				force: true,
				nsfwReason: NsfwReason.TopBanner,
				registerInbound: RegisterInbound.HeaderNsfwAction,
			});
		} catch (e) {}

		setLoadingNSFW(false);
	};

	const handleLogin = () => {
		annalsNavLoginClicked();
		openLogin({
			analytics: { inbound: RegisterInbound.LoginModalSwitchToCreate },
		});
	};

	return (
		<>
			<NSFWButton
				size="M"
				loading={loadingNSFW}
				leftIcon={isSafeMode ? <EyeNoneIcon /> : <EyeOpenIcon />}
				onClick={handleSwitchModeClick}
			>
				{t('nsfwButtonTitle')}
			</NSFWButton>
			<ButtonPrimary onClick={handleRegister} size="M">
				{t('signUp')}
			</ButtonPrimary>
			<ButtonIconGhost
				size="M"
				ref={dropdownRef}
				bigIcon={true}
				leftIcon={<DotDotDotIcon />}
			/>
			<Dropdown targetRef={dropdownRef}>
				<DropdownElement onClick={handleLogin} icon={<LoginIcon />}>
					<Text size="M" align="left">
						{t('logIn')}
					</Text>
				</DropdownElement>
				<MobileDropdownSeparator />
				<MobileDropdownElement
					hint={isSafeMode ? t('nsfwShow') : t('nsfwHide')}
					onClick={handleSwitchModeClick}
					icon={isSafeMode ? <EyeNoneIcon /> : <EyeOpenIcon />}
				>
					<Text size="M" align="left">
						{t('nsfwTitle')}
					</Text>
				</MobileDropdownElement>
				<DropdownSeparator />
				<DropdownElement
					data-name="language"
					icon={<WebIcon />}
					onClick={languageOpen}
					hint={`${language} - ${locale.toUpperCase()}`}
				>
					<Text size="M" align="left">
						{t('languageTitle')}
					</Text>
				</DropdownElement>
			</Dropdown>
		</>
	);
};
